/**
 * What a room is playing, as wire data, and the one place that data becomes
 * the numbers a simulation is built from.
 *
 * `DuelRules` — a variant, a hardness, a goal — is chosen by whoever creates
 * the room and carried on `room`, `roundStart` and `resume`. Both ends hand
 * the same bytes to `duelRuleSet` and `duelTickRule` here and get the same
 * engine options and the same tick rule back, so "hard" and "duelSurvival"
 * cannot mean two things (`docs/decisions/0023`).
 *
 * This file is a table and nothing else: it maps names to integers. The rules
 * themselves — what a rising floor does to a board, on which tick — are
 * `@interlock/sim`'s, and the hardness and goal tables are
 * `@interlock/modes`', shared with the solo modes so the two cannot drift
 * (`docs/decisions/0021`, `0050`).
 */
import type { CreateGameOptions } from '@interlock/engine';
import {
  DEFAULT_HARDNESS,
  HARDNESS_GRAVITY_PERMILLE,
  HARDNESS_IDS,
  SPRINT_DEFAULT_LINE_GOAL,
  SPRINT_LINE_GOALS,
  SPRINT_TIME_GOALS_MIN,
  describeGoal,
  describeHardness,
  survivalFloorTicks,
} from '@interlock/modes';
import type { Hardness, SprintGoal } from '@interlock/modes';
import { risingFloorRule } from '@interlock/sim';
import type { TickRule } from '@interlock/sim';

/** `docs/DESIGN.md`, "Versus": the five duels that exist today. */
export type DuelVariant = 'duel' | 'duelSprint' | 'duelSurvival' | 'curses' | 'tug';

/** `docs/DESIGN.md`, "Co-op": two players, one well, one stack. */
export type CoopVariant = 'coopShared' | 'coopWide';

/**
 * Every game a room can be playing.
 *
 * One union rather than two, because a room is a room: the seat allocation, the
 * grace windows, the checkpoint, the relay and the hash ledger are the same
 * machinery whichever of these is on the wire. What co-op changes is how many
 * *boards* there are — one instead of two — and that is a field of the resolved
 * rule set below, not a second kind of room.
 */
export type RoomVariant = DuelVariant | CoopVariant;

/** Spec order, and the order the room-create screen offers them in. */
export const DUEL_VARIANTS: readonly DuelVariant[] = [
  'duel',
  'duelSprint',
  'duelSurvival',
  'curses',
  'tug',
];
export const COOP_VARIANTS: readonly CoopVariant[] = ['coopShared', 'coopWide'];
export const ROOM_VARIANTS: readonly RoomVariant[] = [...DUEL_VARIANTS, ...COOP_VARIANTS];

/** Classic duel stays the default: an unconfigured room is the game phase 04 shipped. */
export const DEFAULT_DUEL_VARIANT: DuelVariant = 'duel';
export const DEFAULT_COOP_VARIANT: CoopVariant = 'coopShared';

/**
 * `docs/DESIGN.md`: `wide` is "a 16-column well, one player per half". The
 * engine has taken a `width` since phase 01; this is the one place the number
 * sixteen appears, and it reaches both ends through `engineOptions` — which is
 * inside the board hash, so neither end can be playing a different well.
 */
export const COOP_WIDE_WIDTH = 16;

/** Two players, two active pieces, one well. Every co-op variant, today. */
export const COOP_SLOTS = 2;

const VARIANT_NAME: Readonly<Record<RoomVariant, string>> = {
  duel: 'Duel',
  duelSprint: 'Duel Sprint',
  duelSurvival: 'Duel Survival',
  curses: 'Curses',
  tug: 'Tug',
  coopShared: 'Co-op Shared',
  coopWide: 'Co-op Wide',
};

const VARIANT_TAGLINE: Readonly<Record<RoomVariant, string>> = {
  duel: 'Clears send garbage. Last one standing wins.',
  duelSprint: 'Same pieces, no garbage. First to the target wins.',
  duelSurvival: 'A rising floor and the attack table. Last one standing wins.',
  curses: 'Clears trigger effects, not garbage. Pick a loadout of three.',
  tug: 'One shared rope. Every clear pulls it your way.',
  coopShared: 'Two pieces, one ten-wide well. Mind each other.',
  coopWide: 'Sixteen columns, half each, one stack. The floor keeps rising.',
};

export function duelVariantName(variant: RoomVariant): string {
  return VARIANT_NAME[variant];
}

export function duelVariantTagline(variant: RoomVariant): string {
  return VARIANT_TAGLINE[variant];
}

/**
 * What a room is playing, as it crosses the wire.
 *
 * Deliberately the same shape as `@interlock/modes`' `ModeConfig` minus the
 * solo-only `modeId`: a variant, a hardness that every variant reads, and a
 * goal that only `duelSprint` has. Plain data — no function, no engine
 * reference — so it round-trips through JSON unchanged and a server can
 * validate it without constructing anything.
 */
export interface DuelRules {
  readonly variant: RoomVariant;
  readonly hardness: Hardness;
  /**
   * `duelSprint`'s lines-or-time target, and co-op's shared lines target.
   * Absent — never `undefined` — on the variants that have no goal to set.
   */
  readonly goal?: SprintGoal;
}

export function isDuelVariant(value: unknown): value is DuelVariant {
  return typeof value === 'string' && (DUEL_VARIANTS as readonly string[]).includes(value);
}

export function isCoopVariant(value: unknown): value is CoopVariant {
  return typeof value === 'string' && (COOP_VARIANTS as readonly string[]).includes(value);
}

export function isRoomVariant(value: unknown): value is RoomVariant {
  return isDuelVariant(value) || isCoopVariant(value);
}

function isHardness(value: unknown): value is Hardness {
  return typeof value === 'string' && (HARDNESS_IDS as readonly string[]).includes(value);
}

/** True for the variants that take a goal at all. */
function hasGoal(variant: RoomVariant): boolean {
  return variant === 'duelSprint' || isCoopVariant(variant);
}

/** `docs/DESIGN.md`, "curses": attacks are effects, never garbage. */
export function isCursesVariant(variant: RoomVariant): boolean {
  return variant === 'curses';
}

/** `docs/DESIGN.md`, "tug": no garbage, only the shared rope. */
export function isTugVariant(variant: RoomVariant): boolean {
  return variant === 'tug';
}

/**
 * The default configuration for a variant.
 *
 * `duelSprint`'s default goal is the solo default (40 lines), so "pick sprint
 * and press go" produces the same race everybody already knows the length of.
 * Co-op takes the same default for the same reason — `docs/prompts/06-coop.md`:
 * "a lines target together, default 40, reusing ModeConfig goals where
 * sensible".
 */
export function defaultDuelRules(variant: RoomVariant = DEFAULT_DUEL_VARIANT): DuelRules {
  if (hasGoal(variant)) {
    return {
      variant,
      hardness: DEFAULT_HARDNESS,
      goal: { kind: 'lines', lines: SPRINT_DEFAULT_LINE_GOAL },
    };
  }
  return { variant, hardness: DEFAULT_HARDNESS };
}

function parseGoal(raw: unknown): SprintGoal | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<SprintGoal>;
  if (candidate.kind === 'lines') {
    const lines = (candidate as { lines?: unknown }).lines;
    return typeof lines === 'number' && SPRINT_LINE_GOALS.includes(lines)
      ? { kind: 'lines', lines }
      : null;
  }
  if (candidate.kind === 'time') {
    const ms = (candidate as { ms?: unknown }).ms;
    return typeof ms === 'number' && SPRINT_TIME_GOALS_MIN.includes(ms / 60_000)
      ? { kind: 'time', ms }
      : null;
  }
  return null;
}

/**
 * Read a room's rules off a request or a checkpoint, defaulting rather than
 * refusing.
 *
 * Defaulting is the right failure here and not laziness: the only way to send
 * a malformed variant is to be a build that does not speak this protocol, and
 * the version gate has already refused those. What is left is an old
 * checkpoint or a hand-written `curl`, and neither should cost anyone a room.
 * The values themselves are checked against the *same* tables the mode-select
 * screen offers, so a room can never be created with a goal or a hardness the
 * UI has no way to describe.
 */
export function parseDuelRules(raw: unknown): DuelRules {
  if (typeof raw !== 'object' || raw === null) return defaultDuelRules();
  const candidate = raw as Partial<DuelRules>;
  const variant = isRoomVariant(candidate.variant) ? candidate.variant : DEFAULT_DUEL_VARIANT;
  const hardness = isHardness(candidate.hardness) ? candidate.hardness : DEFAULT_HARDNESS;
  if (!hasGoal(variant)) return { variant, hardness };
  // Co-op's goal is a lines target and only a lines target. A shared clock
  // would need a "most lines when it expires" tie-break, and there is nobody to
  // break a tie against: the team either got there or it did not.
  const parsed = parseGoal(candidate.goal);
  const usable = isCoopVariant(variant) && parsed?.kind !== 'lines' ? null : parsed;
  const goal = usable ?? defaultDuelRules(variant).goal;
  return goal === undefined ? { variant, hardness } : { variant, hardness, goal };
}

/**
 * The rules, resolved into the numbers the server and the driver actually
 * read. One function, called on both ends, so "hard" cannot mean two things.
 */
export interface DuelRuleSet {
  readonly variant: RoomVariant;
  /** Merged with `{ seed }` and passed to `createGame` — by *both* ends. */
  readonly engineOptions: Readonly<Omit<CreateGameOptions, 'seed'>>;
  /** Ticks between rising-floor rows, or `null` when this variant has no floor. */
  readonly floorEveryTicks: number | null;
  /**
   * Whether clears send garbage. False for `duelSprint` (a pure race),
   * `curses` (attacks are effects, not garbage) and `tug` (the rope, not
   * garbage, carries the pressure).
   */
  readonly attack: boolean;
  /** True for `curses`: a clear that would send attack issues one instead. */
  readonly curses: boolean;
  /** True for `tug`: a clear pulls the shared rope instead of sending garbage. */
  readonly tug: boolean;
  /** Lines that win a `duelSprint` round or finish a co-op run, or `null`. */
  readonly linesTarget: number | null;
  /** Milliseconds after which a `duelSprint` round is decided on lines, or `null`. */
  readonly timeLimitMs: number | null;
  /**
   * True when the two seats share one well. The single flag anything outside
   * this module branches on: one board instead of two, one lines total instead
   * of two, one ending for both players.
   */
  readonly coop: boolean;
  /** Active pieces in a well. One per player in co-op, one in total otherwise. */
  readonly slots: number;
}

export function duelRuleSet(rules: DuelRules): DuelRuleSet {
  const speedPermille = HARDNESS_GRAVITY_PERMILLE[rules.hardness];
  const goal = hasGoal(rules.variant) ? rules.goal : undefined;
  const coop = isCoopVariant(rules.variant);

  return {
    variant: rules.variant,
    engineOptions: Object.freeze({
      gravity: { speedPermille },
      ...(coop ? { slots: COOP_SLOTS } : {}),
      ...(rules.variant === 'coopWide' ? { width: COOP_WIDE_WIDTH } : {}),
    }),
    // `docs/DESIGN.md`: "the rising floor for both players at the configured
    // cadence." The cadence is the solo survival table in whole ticks
    // (10s/8s/6s/4s -> 600/480/360/240), from the same function solo survival
    // reads.
    //
    // `coopWide` reads the *same* table, and that is the whole of
    // `docs/prompts/06-coop.md`'s "garbage from the server rather than from
    // each other ... pick a sane cadence tied to hardness". Reusing the numbers
    // rather than inventing a second set is the point: two players clear a
    // sixteen-wide row at roughly twice a solo player's rate against a row that
    // is roughly twice the work, so the pressure a hardness level *means* comes
    // out about where it does in solo survival. Whether it is any fun is a
    // question only play answers — `docs/STATE.md` records it as open.
    //
    // `coopShared` has no floor at all: two pieces in one ten-wide well is
    // already the pressure, and a rising floor under two people fighting for
    // the same columns would decide the round before either of them made a
    // mistake.
    floorEveryTicks:
      rules.variant === 'duelSurvival' || rules.variant === 'coopWide'
        ? survivalFloorTicks(rules.hardness)
        : null,
    // Nobody to attack in co-op: there is one board and the clears are the
    // team's. `state.attack` is still computed, because it is inside the hash
    // and both ends must agree on it; it simply has nowhere to go. `curses`
    // and `tug` are the same shape of "computed but redirected" — a clear's
    // attack becomes an effect or a rope pull instead of a batch.
    attack:
      rules.variant !== 'duelSprint' &&
      rules.variant !== 'curses' &&
      rules.variant !== 'tug' &&
      !coop,
    curses: isCursesVariant(rules.variant),
    tug: isTugVariant(rules.variant),
    linesTarget: goal?.kind === 'lines' ? goal.lines : null,
    timeLimitMs: goal?.kind === 'time' ? goal.ms : null,
    coop,
    slots: coop ? COOP_SLOTS : 1,
  };
}

/**
 * The tick rule for a room's rules, or `null` when the variant has none: the
 * rising floor at the room's cadence, for `duelSurvival` and `coopWide`.
 *
 * The rule is `@interlock/sim`'s `risingFloorRule` — the one implementation,
 * which solo survival runs too. What this function adds is which variants get
 * it and how often.
 */
export function duelTickRule(rules: DuelRules): TickRule | null {
  const everyTicks = duelRuleSet(rules).floorEveryTicks;
  return everyTicks === null ? null : risingFloorRule(everyTicks);
}

/** The configuration half only — "Hard · 40 lines" — for a screen that already names the variant. */
export function describeDuelConfig(rules: DuelRules): string {
  const parts = [describeHardness(rules.hardness)];
  const goal = hasGoal(rules.variant) ? describeGoal(rules.goal) : null;
  if (goal !== null) parts.push(goal);
  return parts.join(' · ');
}
